<template>
  <div v-if="view" v-bind="$attrs" :class="{ 'flex justify-center': rail }" data-test="sidebar-trial-status">
    <button
      v-if="rail"
      ref="anchor"
      type="button"
      class="flex size-10 items-center justify-center rounded-lg border transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary motion-reduce:transition-none"
      :class="[railTone[view.tone], railClickable ? 'cursor-pointer' : 'cursor-default']"
      :aria-label="railLabel"
      :aria-haspopup="view.info ? 'dialog' : undefined"
      :aria-expanded="view.info ? popover === 'pinned' : undefined"
      data-test="trial-status-rail"
      @click="onRailClick"
      @mouseenter="showHover"
      @mouseleave="scheduleHide"
      @focus="showHover"
      @blur="onAnchorBlur">
      <ClockIcon v-if="view.railIcon === 'clock'" class="size-5 text-warning" aria-hidden="true" />
      <CheckCircleIcon v-else-if="view.railIcon === 'check'" class="size-5 text-positive" aria-hidden="true" />
      <PauseCircleIcon v-else-if="view.railIcon === 'pause'" class="size-5 text-danger" aria-hidden="true" />
      <span v-else class="relative flex size-7 items-center justify-center" aria-hidden="true">
        <svg viewBox="0 0 28 28" class="absolute inset-0 size-7 -rotate-90" :class="ringTone[view.tone]">
          <circle
            cx="14"
            cy="14"
            r="12.75"
            fill="none"
            stroke="currentColor"
            stroke-opacity="0.22"
            stroke-width="2.5" />
          <circle
            cx="14"
            cy="14"
            r="12.75"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            pathLength="100"
            :stroke-dasharray="`${ringPercent} 100`" />
        </svg>
        <span class="relative text-[11px] leading-none font-bold" :class="railNumberTone[view.tone]">
          {{ daysLeft ?? 0 }}
        </span>
      </span>
    </button>

    <button
      v-else-if="view.info"
      ref="anchor"
      type="button"
      class="flex w-full cursor-pointer flex-col gap-1.5 rounded-lg px-3 pt-2.5 pb-3 text-left transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary motion-reduce:transition-none"
      :class="cardTone[view.tone]"
      aria-haspopup="dialog"
      :aria-expanded="popover === 'pinned'"
      data-test="trial-status-card"
      @click="togglePinned">
      <span class="flex items-center gap-2">
        <CheckCircleIcon class="size-4 shrink-0 text-positive" aria-hidden="true" />
        <span class="text-[13px] leading-[18px] font-semibold" :class="titleTone[view.tone]">{{ view.title }}</span>
      </span>
      <span class="text-xs leading-[17px] text-pretty text-copy">{{ view.body }}</span>
    </button>

    <div
      v-else
      role="status"
      class="flex w-full flex-col gap-1.5 rounded-lg px-3 pt-2.5 pb-3 text-left"
      :class="cardTone[view.tone]"
      data-test="trial-status-card">
      <div class="flex items-center gap-2">
        <PauseCircleIcon v-if="view.cardIcon === 'pause'" class="size-4 shrink-0 text-danger" aria-hidden="true" />
        <CheckCircleIcon
          v-else-if="view.cardIcon === 'check'"
          class="size-4 shrink-0 text-positive"
          aria-hidden="true" />
        <svg
          v-else
          viewBox="0 0 16 16"
          class="size-4 shrink-0 -rotate-90"
          :class="ringTone[view.tone]"
          aria-hidden="true"
          data-test="trial-status-ring">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-opacity="0.22" stroke-width="2" />
          <circle
            cx="8"
            cy="8"
            r="7"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            pathLength="100"
            :stroke-dasharray="`${ringPercent} 100`" />
        </svg>
        <span class="text-[13px] leading-[18px] font-semibold" :class="titleTone[view.tone]">{{ view.title }}</span>
      </div>
      <p v-if="view.body" class="text-xs leading-[17px] text-pretty text-copy">{{ view.body }}</p>
      <template v-if="ctaText">
        <button
          v-if="view.ctaStyle === 'link'"
          type="button"
          class="mt-0.5 inline-flex cursor-pointer items-center gap-1 self-start text-[13px] leading-[18px] font-bold text-primary transition-colors duration-150 hover:text-(--rm-primary-strong) focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary motion-reduce:transition-none"
          data-test="trial-status-cta"
          @click="openBilling">
          {{ ctaText }}
          <ArrowRightIcon class="size-3.5" aria-hidden="true" />
        </button>
        <button
          v-else-if="view.ctaStyle === 'outlined'"
          type="button"
          class="mt-1 inline-flex h-[30px] w-full cursor-pointer items-center justify-center gap-1 rounded-md border border-warning/45 bg-white text-[13px] font-bold text-amber-700 transition-colors duration-150 hover:bg-warning-soft focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary motion-reduce:transition-none dark:bg-panel dark:text-warning"
          data-test="trial-status-cta"
          @click="openBilling">
          {{ ctaText }}
          <ArrowRightIcon v-if="hasSavedCard" class="size-3.5" aria-hidden="true" />
        </button>
        <Button v-else size="small" class="mt-1 w-full" data-test="trial-status-cta" @click="openBilling">
          {{ ctaText }}
          <ArrowRightIcon v-if="hasSavedCard" class="size-3.5" aria-hidden="true" />
        </Button>
      </template>
    </div>
  </div>

  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out motion-reduce:transition-none"
      enter-from-class="-translate-x-1 opacity-0 motion-reduce:translate-x-0"
      leave-active-class="transition duration-150 ease-in motion-reduce:transition-none"
      leave-to-class="opacity-0">
      <div
        v-if="view && popover"
        ref="popoverEl"
        role="dialog"
        :aria-label="view.info ? 'Billing is set' : view.title"
        class="fixed z-1300 rounded-lg border border-line bg-white px-4 py-3.5 text-left shadow-popover dark:bg-panel dark:shadow-none"
        :style="popoverStyle"
        data-test="trial-status-popover"
        @mouseenter="cancelHide"
        @mouseleave="scheduleHide"
        @focusout="onPopoverFocusOut">
        <div v-if="view.info" class="flex flex-col gap-1.5">
          <p class="flex items-center gap-2 text-sm leading-5 font-bold text-heading">
            <CheckCircleIcon class="size-[18px] shrink-0 text-positive" aria-hidden="true" />
            Billing is set
          </p>
          <p class="text-[13px] leading-[19px] text-copy">First charge {{ amount }} on {{ longEndDate }}.</p>
          <router-link
            :to="{ name: ROUTES.SETTINGS_BILLING }"
            class="cursor-pointer self-start text-[13px] leading-[19px] font-bold text-primary transition-colors duration-150 hover:text-(--rm-primary-strong) focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary motion-reduce:transition-none"
            @click="closePopover">
            Manage in Billing →
          </router-link>
        </div>
        <div v-else class="flex flex-col gap-1">
          <p class="text-[13px] leading-[18px] font-bold text-heading">{{ view.title }}</p>
          <p v-if="view.body" class="text-xs leading-[17px] text-pretty text-copy">{{ view.body }}</p>
          <button
            v-if="ctaText"
            type="button"
            class="mt-1 inline-flex cursor-pointer items-center gap-1 self-start text-[13px] leading-[18px] font-bold text-primary transition-colors duration-150 hover:text-(--rm-primary-strong) focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary motion-reduce:transition-none"
            @click="openBilling">
            {{ ctaText }}
            <ArrowRightIcon class="size-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ArrowRightIcon, CheckCircleIcon, ClockIcon, PauseCircleIcon } from '@heroicons/vue/24/outline'
import { useFeatureGating } from '@dashboard/composables/useFeatureGating'
import { useTrialStatus } from '@dashboard/composables/useTrialStatus'
import { formatLongDate, formatShortDate } from '@dashboard/utils/trialDates'
import { ROUTES } from '@dashboard/router/routeNames'
import { useBillingDialogStore } from '@dashboard/stores/billingDialogStore'
import { onClickOutside, useElementBounding, useEventListener, useWindowSize } from '@vueuse/core'
import Button from 'primevue/button'
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{ rail?: boolean }>(), { rail: false })

type Tone = 'neutral' | 'warn' | 'warnStrong' | 'danger' | 'calm'
type StatusIcon = 'ring' | 'clock' | 'check' | 'pause'
type CtaStyle = 'link' | 'outlined' | 'filled'

type StatusView = {
  title: string
  body: string | null
  tone: Tone
  cardIcon: StatusIcon
  railIcon: StatusIcon
  ctaStyle: CtaStyle | null
  /** Activated and card trials, for billing managers: the status toggles the "Billing is set" popover. */
  info: boolean
}

const LOCK_SENTENCE =
  'When your trial expires, your account will be locked out and any RightMessage changes to your website will no longer work.'
const EXPIRED_SENTENCE = 'Your account is locked and RightMessage changes to your website no longer work.'
const TRIAL_DAYS = 14

const { state, daysLeft, endsAt, renewalPlan, hasSavedCard, canManageBilling, ownerFirstName } = useTrialStatus()
const { tierDisplayName } = useFeatureGating()
const billingDialog = useBillingDialogStore()

const shortEndDate = computed(() => (endsAt.value ? formatShortDate(endsAt.value) : null))
const longEndDate = computed(() => (endsAt.value ? formatLongDate(endsAt.value) : ''))
const amount = computed(() => renewalPlan.value?.amount ?? '')

const view = computed<StatusView | null>(() => {
  const days = daysLeft.value ?? 0
  const daysLeftLabel = days === 1 ? '1 day left' : `${days} days left`
  switch (state.value) {
    case 'early':
      return {
        title: `${daysLeftLabel} in your trial`,
        body: LOCK_SENTENCE,
        tone: 'neutral',
        cardIcon: 'ring',
        railIcon: 'ring',
        ctaStyle: 'link',
        info: false
      }
    case 'last':
      return {
        title: `${daysLeftLabel} in your trial`,
        body: LOCK_SENTENCE,
        tone: 'warn',
        cardIcon: 'ring',
        railIcon: 'ring',
        ctaStyle: 'outlined',
        info: false
      }
    case 'today':
      return {
        title: 'Your trial ends today',
        body: LOCK_SENTENCE,
        tone: 'warnStrong',
        cardIcon: 'ring',
        railIcon: 'clock',
        ctaStyle: 'filled',
        info: false
      }
    case 'expired':
      return {
        title: shortEndDate.value ? `Your trial ended ${shortEndDate.value}` : 'Your trial ended',
        body: EXPIRED_SENTENCE,
        tone: 'danger',
        cardIcon: 'pause',
        railIcon: 'pause',
        ctaStyle: 'filled',
        info: false
      }
    case 'activated':
    case 'cardtrial': {
      const plan = renewalPlan.value
      if (!plan) {
        return null
      }
      const planName = tierDisplayName(plan.tier)
      return {
        title: shortEndDate.value
          ? `Your RightMessage plan starts on ${shortEndDate.value}`
          : 'Your RightMessage plan is set',
        body: `There’s nothing you need to do. Once your trial ends, you’ll start your ${planName} subscription.`,
        tone: 'calm',
        cardIcon: 'check',
        railIcon: 'check',
        ctaStyle: null,
        info: canManageBilling.value
      }
    }
    case 'collab':
      return {
        title: days === 0 ? 'Trial · ends today' : `Trial · ${daysLeftLabel}`,
        body: ownerFirstName.value
          ? `Only ${ownerFirstName.value} (the project owner) can set up billing.`
          : 'Only the project owner can set up billing.',
        tone: 'neutral',
        cardIcon: 'ring',
        railIcon: 'ring',
        ctaStyle: null,
        info: false
      }
    default:
      return null
  }
})

const ctaText = computed(() => {
  if (!view.value?.ctaStyle || !canManageBilling.value) {
    return null
  }
  return hasSavedCard.value ? 'Confirm your plan' : 'Enter your card details'
})

const ringPercent = computed(() =>
  state.value === 'today' ? 5 : Math.max(0, Math.min(100, Math.round(((daysLeft.value ?? 0) / TRIAL_DAYS) * 100)))
)

const railClickable = computed(() => !!view.value?.info || !!ctaText.value)

const railLabel = computed(() => {
  if (!view.value) {
    return ''
  }
  return `${view.value.title}. ${ctaText.value ?? view.value.body ?? ''}`.trim()
})

const cardTone: Record<Tone, string> = {
  neutral: 'bg-white shadow-sm ring-1 ring-[rgb(38_35_58/0.1)] dark:bg-panel dark:shadow-none dark:ring-line',
  warn: 'bg-warning-soft ring-1 ring-warning/35',
  warnStrong: 'bg-warning-soft ring-1 ring-warning',
  danger: 'bg-danger-soft ring-1 ring-danger/35',
  calm: 'ring-1 ring-line hover:bg-tint'
}

const titleTone: Record<Tone, string> = {
  neutral: 'text-heading',
  warn: 'text-amber-700 dark:text-warning',
  warnStrong: 'text-amber-700 dark:text-warning',
  danger: 'text-red-700 dark:text-danger',
  calm: 'text-heading'
}

const ringTone: Record<Tone, string> = {
  neutral: 'text-primary',
  warn: 'text-warning',
  warnStrong: 'text-warning',
  danger: 'text-danger',
  calm: 'text-positive'
}

const railTone: Record<Tone, string> = {
  neutral: 'border-transparent hover:bg-tint',
  warn: 'border-warning/40 bg-warning-soft hover:bg-warning/15',
  warnStrong: 'border-warning bg-warning-soft hover:bg-warning/15',
  danger: 'border-danger/40 bg-danger-soft hover:bg-danger/15',
  calm: 'border-transparent hover:bg-tint'
}

const railNumberTone: Record<Tone, string> = {
  neutral: 'text-copy',
  warn: 'text-amber-700 dark:text-warning',
  warnStrong: 'text-amber-700 dark:text-warning',
  danger: 'text-red-700 dark:text-danger',
  calm: 'text-copy'
}

/** `hover` follows the rail button's hover/focus; `pinned` stays until toggled, Esc or an outside click. */
const popover = ref<'hover' | 'pinned' | null>(null)
const anchor = useTemplateRef<HTMLElement>('anchor')
const popoverEl = useTemplateRef<HTMLElement>('popoverEl')
const bounds = useElementBounding(anchor)
const viewport = useWindowSize()
let hideTimer: ReturnType<typeof setTimeout> | undefined

const popoverStyle = computed(() => {
  const gap = props.rail ? 10 : 12
  const width = Math.min(props.rail ? 248 : 280, viewport.width.value - 16)
  if (bounds.right.value + gap + width <= viewport.width.value - 8) {
    return {
      width: `${width}px`,
      left: `${bounds.right.value + gap}px`,
      bottom: `${viewport.height.value - bounds.bottom.value}px`
    }
  }
  // Narrow viewports (the mobile menu drawer): open above the card instead of off-screen.
  return {
    width: `${width}px`,
    left: `${Math.max(8, Math.min(bounds.left.value, viewport.width.value - width - 8))}px`,
    bottom: `${viewport.height.value - bounds.top.value + 8}px`
  }
})

function cancelHide() {
  clearTimeout(hideTimer)
}

function closePopover() {
  cancelHide()
  popover.value = null
}

function showHover() {
  if (!props.rail) {
    return
  }
  cancelHide()
  if (!popover.value) {
    bounds.update()
    popover.value = 'hover'
  }
}

function scheduleHide() {
  if (popover.value !== 'hover') {
    return
  }
  cancelHide()
  hideTimer = setTimeout(() => {
    if (popover.value === 'hover') {
      popover.value = null
    }
  }, 150)
}

function togglePinned() {
  if (popover.value === 'pinned') {
    closePopover()
    return
  }
  cancelHide()
  bounds.update()
  popover.value = 'pinned'
  void nextTick(() => popoverEl.value?.querySelector<HTMLElement>('a, button')?.focus())
}

function openBilling() {
  closePopover()
  billingDialog.open({ mode: 'keep', source: 'sidebar' })
}

function onRailClick() {
  if (view.value?.info) {
    togglePinned()
  } else if (ctaText.value) {
    openBilling()
  }
}

function onAnchorBlur(event: FocusEvent) {
  if (event.relatedTarget instanceof Node && popoverEl.value?.contains(event.relatedTarget)) {
    return
  }
  scheduleHide()
}

function onPopoverFocusOut(event: FocusEvent) {
  const next = event.relatedTarget
  if (next instanceof Node && (popoverEl.value?.contains(next) || anchor.value?.contains(next))) {
    return
  }
  scheduleHide()
}

useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape' && popover.value) {
    anchor.value?.focus()
    closePopover()
  }
})

onClickOutside(
  popoverEl,
  () => {
    if (popover.value === 'pinned') {
      closePopover()
    }
  },
  { ignore: [anchor] }
)

watch([() => props.rail, state], closePopover)

onBeforeUnmount(cancelHide)
</script>
